import { useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INK = "#000000";
const BG = "#FFFFFF";
const MUTE = "#666666";
const DISPLAY = "'Archivo Black', 'Inter', system-ui, sans-serif";
const BODY = "'Inter', system-ui, -apple-system, sans-serif";

export default function BenchBookingSuccessView({ bookingId }) {
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState("");
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    document.title = "Booking confirmed — HEY SOVA";
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    let attempts = 0;
    let alive = true;
    async function poll() {
      try {
        const url = sessionId
          ? `${API}/bench/booking/${bookingId}?session_id=${encodeURIComponent(sessionId)}`
          : `${API}/bench/booking/${bookingId}`;
        const { data } = await axios.get(url);
        if (!alive) return;
        setBooking(data);
        if (data.status === "paid") {
          if (!opened && data.cal_link) {
            window.open(data.cal_link, "_blank", "noopener");
            setOpened(true);
          }
          return;
        }
        attempts += 1;
        if (attempts < 10) setTimeout(poll, 1500);
        else setError("Payment is taking longer than expected to confirm. Please refresh.");
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Couldn't fetch booking status.");
      }
    }
    poll();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  const wrap = { minHeight: "100vh", background: BG, color: INK, fontFamily: BODY, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 };

  if (error) {
    return (
      <div style={wrap} data-testid="booking-error">
        <div style={{ maxWidth: 540, textAlign: "center" }}>
          <h1 style={{ fontFamily: DISPLAY, fontSize: 48, lineHeight: 0.95, letterSpacing: "-0.025em", textTransform: "uppercase", margin: "0 0 18px" }}>
            Something went wrong.
          </h1>
          <p style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.5 }}>{error}</p>
          <a href="/" style={{ display: "inline-block", marginTop: 24, padding: "14px 22px", border: `2px solid ${INK}`, background: INK, color: BG, textDecoration: "none", fontWeight: 800, fontSize: 13, letterSpacing: "0.06em", textTransform: "uppercase" }}>
            ← Back to Hey Sova
          </a>
        </div>
      </div>
    );
  }

  if (!booking || booking.status !== "paid") {
    return (
      <div style={wrap} data-testid="booking-pending">
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.22em", textTransform: "uppercase", marginBottom: 14 }}>
            Confirming payment
          </div>
          <div style={{ fontFamily: DISPLAY, fontSize: 36, textTransform: "uppercase", letterSpacing: "-0.02em" }}>
            One moment…
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={wrap} data-testid="booking-success">
      <div style={{ maxWidth: 600, width: "100%" }}>
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.22em", textTransform: "uppercase", marginBottom: 14 }}>
          Payment confirmed · ${booking.amount_usd}
        </div>
        <h1 style={{ fontFamily: DISPLAY, fontSize: "clamp(40px, 6vw, 64px)", lineHeight: 0.92, letterSpacing: "-0.03em", textTransform: "uppercase", margin: "0 0 20px" }}>
          Now pick a time<br />with <span style={{ background: INK, color: BG, padding: "0 0.1em" }}>{booking.expert_name}</span>.
        </h1>
        <p style={{ fontSize: 16, fontWeight: 600, color: INK, lineHeight: 1.55, marginBottom: 28 }}>
          We've opened {booking.expert_name}'s booking calendar in a new tab. If it didn't open,
          use the button below. You'll get a confirmation email with the calendar invite once
          the time is locked.
        </p>
        {booking.cal_link ? (
          <a
            href={booking.cal_link}
            target="_blank"
            rel="noreferrer"
            data-testid="open-cal-btn"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              border: `2px solid ${INK}`,
              background: INK,
              color: BG,
              padding: "18px 24px",
              fontFamily: BODY,
              fontWeight: 800,
              fontSize: 14,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              textDecoration: "none",
            }}
          >
            Open booking calendar →
          </a>
        ) : (
          <div style={{ padding: "16px 18px", border: `2px solid ${INK}`, fontSize: 14, fontWeight: 600, background: "#FFF8DC" }}>
            {booking.expert_name} doesn't have a Cal.com link yet. We've notified them — expect
            an email to schedule within one business day.
          </div>
        )}
        <p style={{ marginTop: 32, fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: MUTE, lineHeight: 1.6 }}>
          Refunds available only if the expert no-shows the scheduled session. A receipt has
          been emailed to you.
        </p>
        <a href="/" style={{ display: "inline-block", marginTop: 24, fontSize: 11, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: INK, textDecoration: "underline", textUnderlineOffset: 3 }}>
          ← Back to Hey Sova
        </a>
      </div>
    </div>
  );
}
